import {
  buildLocalRegistryMiniSearch,
  searchLocalRegistryMiniSearch,
} from './buildLocalRegistryMiniSearch'

// Curated static navigation entries. Each entry can ship a `keywords` array
// for synonyms / abbreviations (e.g. "history" → Reports). The entries are
// rendered as Algolia-shaped hits with __nav: true and __localOrigin: true.
//
// Add `newTab: true` to an entry whose route should open in a new tab, matching
// how the side nav treats it.
//
// Add `prefill: true` to an entry to surface it in the dropdown empty-state
// (focused, no query). None are flagged today — navigation only appears while
// typing — but the empty-state builder gathers prefill-flagged entries across
// all registries, so this is ready for future use.

export const STATIC_NAV_ENTRIES = [
  {
    id: 'nav-now-playing', label: 'Now Playing', path: null, action: 'nowPlaying', icon: 'compass', keywords: ['now playing', 'live', 'present', 'play', 'session', 'live view'], prefill: true,
  },
  {
    id: 'nav-recent', label: 'Recent content', path: '/recent', icon: 'history', keywords: ['home', 'history'], prefill: true,
  },
  {
    id: 'nav-library', label: 'Library', path: '/library', icon: 'library', keywords: ['my content', 'sets', 'questions', 'folders'], prefill: true,
  },
  {
    id: 'nav-reports', label: 'Reports', path: '/reports', icon: 'history-inverted', keywords: ['analytics', 'history'],
  },
  {
    id: 'nav-scoresheet', label: 'Scoresheet', path: '/scoresheet', icon: 'scoresheet', keywords: ['gradebook', 'grades'], newTab: true,
  },
  {
    id: 'nav-discover', label: 'Discover', path: '/discover', icon: 'discover', keywords: ['browse', 'public'],
  },
  {
    id: 'nav-trash', label: 'Trash', path: '/trash', icon: 'trash', keywords: ['deleted', 'archived'],
  },
  {
    id: 'nav-account', label: 'Account', path: '/account', icon: 'gear', keywords: ['settings', 'profile', 'billing'],
  },
]

/**
 * Build dynamic navigation entries from Redux collections — one per section
 * (Class — name). Packs are excluded; they appear in the Library search instead.
 *
 * @param {Object} input
 * @param {Array}  input.sections
 * @returns {Array<Object>}
 */
export function buildDynamicNavigationEntries({ sections }) {
  const entries = []
  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      if (!section || !section.id) return
      entries.push({
        id: `nav-class-${section.id}`,
        label: section.name,
        color: section.color,
        path: `/classes/${section.id}`,
        icon: 'class',
        keywords: ['class', 'section', section.name],
      })
    })
  }
  return entries
}

/**
 * Project a raw navigation entry into the Algolia-shaped hit consumed by the
 * dropdown. Used for the empty-state prefill list where no query is run.
 */
export function toNavigationHit(entry) {
  return {
    ...entry,
    objectID: entry.id,
    __nav: true,
    __localOrigin: true,
  }
}

/**
 * Build a MiniSearch navigation index over a precomputed list of entries.
 * Caller is expected to memoise the entries array on its end so this
 * function is a thin allocator.
 */
export function buildNavigationMiniSearchIndex(entries) {
  return buildLocalRegistryMiniSearch(entries)
}

/**
 * Run a query against a MiniSearch navigation index and return Algolia-shaped
 * hits mirroring searchNavigation, but tagged with __localEngine: 'minisearch'
 * and __minisearchScore. The `entries` array is used to recover the full entry
 * fields (path, icon, keywords, color) that MiniSearch does not store.
 */
export function searchNavigationMiniSearch(ms, entries, query, limit = 8) {
  if (!ms || !query) return []
  const byId = new Map((Array.isArray(entries) ? entries : []).map((e) => [e.id, e]))
  return searchLocalRegistryMiniSearch(ms, query, undefined, limit).map((result) => {
    const entry = byId.get(result.id) || { id: result.id, label: result.label }
    return {
      ...entry,
      objectID: entry.id,
      __nav: true,
      __localOrigin: true,
      __localEngine: 'minisearch',
      __minisearchScore: result.score,
      __minisearchTerms: result.matchedTerms,
    }
  })
}
